// Contrato del alta self-service (HU-001, CU-001): lo valida la api (POST /v1/signup) y lo
// usa el formulario del panel. Los mensajes son textos de la UI (español).
import { z } from 'zod';
import { SLUG_MAX_LENGTH, SLUG_MIN_LENGTH, SLUG_PATTERN } from './slug.js';

export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 128;
/** RN-02: todo tenant nace con prueba gratuita de 14 días. */
export const TRIAL_DAYS = 14;

export const signupSchema = z.object({
  name: z
    .string({ error: 'Escribe tu nombre' })
    .trim()
    .min(1, { error: 'Escribe tu nombre' })
    .max(80, { error: 'Máximo 80 caracteres' }),
  email: z
    .string({ error: 'Escribe tu correo' })
    .trim()
    .toLowerCase()
    .max(254, { error: 'Correo demasiado largo' })
    .pipe(z.email({ error: 'Escribe un correo válido' })),
  password: z
    .string({ error: 'Escribe una contraseña' })
    .min(PASSWORD_MIN_LENGTH, { error: `Mínimo ${PASSWORD_MIN_LENGTH} caracteres` })
    .max(PASSWORD_MAX_LENGTH, { error: `Máximo ${PASSWORD_MAX_LENGTH} caracteres` }),
  storeName: z
    .string({ error: 'Escribe el nombre de tu tienda' })
    .trim()
    .min(2, { error: 'Mínimo 2 caracteres' })
    .max(60, { error: 'Máximo 60 caracteres' }),
  // Mismo formato que slugFormatSchema, con mensajes para la UI. Los reservados NO se
  // rechazan aquí: se responden como "ocupado" con sugerencias (RN-01b).
  slug: z
    .string({ error: 'Elige el link de tu tienda' })
    .min(SLUG_MIN_LENGTH, { error: `Mínimo ${SLUG_MIN_LENGTH} caracteres` })
    .max(SLUG_MAX_LENGTH, { error: `Máximo ${SLUG_MAX_LENGTH} caracteres` })
    .regex(SLUG_PATTERN, {
      error: 'Solo minúsculas, números y guiones (sin guion al inicio ni al final)',
    }),
});

export type SignupInput = z.input<typeof signupSchema>;
export type SignupData = z.output<typeof signupSchema>;

/** Campos del formulario de alta. */
export type SignupField = keyof SignupData;

/** Códigos de error de negocio del alta (cuerpo de las respuestas 4xx). */
export type SignupErrorCode = 'validation_failed' | 'slug_taken' | 'email_taken' | 'rate_limited';

export interface SignupErrorBody {
  code: SignupErrorCode;
  message: string;
  /** Errores por campo (solo validation_failed; vacío si el error no es de un campo). */
  fieldErrors?: Partial<Record<SignupField, string>>;
  /** Slugs libres alternativos (solo slug_taken). */
  suggestions?: string[];
}

/** Tienda tal como la devuelve la api (fechas en ISO 8601). */
export interface StoreSummary {
  id: string;
  slug: string;
  name: string;
  status: 'active' | 'suspended';
  plan: string;
  trialEndsAt: string | null;
  /** https://{slug}.{BASE_DOMAIN} */
  url: string;
}

export interface SignupResult {
  user: { id: string; name: string; email: string };
  tenant: StoreSummary;
  /** false si la cuenta se creó pero no se pudo abrir la sesión (entrar a mano). */
  sessionStarted: boolean;
}

export interface SlugAvailability {
  slug: string;
  available: boolean;
  suggestions: string[];
}

/** Fecha de fin de la prueba gratuita para un alta hecha en `now` (RN-02). */
export function trialEndsAt(now: Date = new Date()): Date {
  return new Date(now.getTime() + TRIAL_DAYS * 24 * 60 * 60 * 1000);
}

/** URL pública de la tienda: https://{slug}.{baseDomain} (ADR-009). */
export function storeUrl(slug: string, baseDomain: string): string {
  return `https://${slug}.${baseDomain}`;
}
